import { castArray } from 'lodash-es';
import { filter, find, map, pick, pipe, piped } from 'remeda';
import invariant from 'tiny-invariant';

import { Element } from '@@editor/helpers';
import {
    ELEMENT_TYPES,
    type ParagraphElement,
    type PollAnswerElement,
    type PollElement,
    type PollQuestionElement,
} from '@@editor/helpers/Element';
import {
    isUnityPoll,
    type UnityParagraph,
    type UnityPoll,
    type UnityPollItem,
} from '@@editor/typings/UnityElements';
import toParagraph from '@@editor/utils/toParagraph';

import { CURRENT_VERSION } from '../constants';
import { createElementFromSingleLineState } from '../deserializeNodes';
import { type ElementRule, type Next } from '../types';

type UnityParagraphWithPollItemId = UnityParagraph & {
    pollItemId?: Id;
};

const stripPollItem = (pollItem: UnityPollItem): UnityParagraphWithPollItemId => ({
    ...pollItem.text,
    pollItemId: pollItem.id,
});

const recreatePollItem =
    (next: Next) =>
    (element?: PollQuestionElement | PollAnswerElement): UnityPollItem => ({
        type: 'poll-item',
        version: CURRENT_VERSION,
        text: next(toParagraph(element)),
        id: element?.data.pollItemId,
    });

const deserializeQuestion = (next: Next<ParagraphElement>) => (question: UnityPoll['question']) =>
    pipe(
        question,
        stripPollItem,
        next,
        castArray,
        createElementFromSingleLineState(ELEMENT_TYPES.POLL_QUESTION),
    );

const deserializeAnswers = (next: Next<ParagraphElement>) => (answers: UnityPoll['answers']) =>
    pipe(
        answers,
        map(
            piped(
                stripPollItem,
                next,
                castArray,
                createElementFromSingleLineState(ELEMENT_TYPES.POLL_ANSWER),
            ),
        ),
    );

const serializeQuestion =
    (next: Next<UnityPoll['question']>) => (children: PollElement['children']) =>
        pipe(children, find(Element.isPollQuestionElement), recreatePollItem(next));

const serializeAnswers =
    (next: Next<UnityPoll['answers'][number]>) => (children: PollElement['children']) =>
        pipe(children, filter(Element.isPollAnswerElement), map(recreatePollItem(next)));

type Rule = ElementRule<PollElement, UnityPoll>;

const pollRules: Rule = {
    deserialize: (element, next): ReturnType<Rule['deserialize']> => {
        if (isUnityPoll(element)) {
            invariant(next, 'Cannot deserialize Poll without a `next` function');

            const { answers, question, type } = element;

            return {
                type,
                data: pick(element, ['templateElement']),
                children: [
                    deserializeQuestion(next)(question),
                    ...deserializeAnswers(next)(answers),
                ],
            };
        }
    },

    serialize: (node, next): ReturnType<Rule['serialize']> => {
        if (Element.isPollElement(node)) {
            invariant(next, 'Cannot serialize Poll without a `next` function');

            const { children, data, type } = node;

            return {
                ...pick(data, ['templateElement']),
                version: CURRENT_VERSION,
                type,
                question: serializeQuestion(next)(children),
                answers: serializeAnswers(next)(children),
            };
        }
    },
};

export default pollRules;
